import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type {
  AuthorshipClass,
  DocumentDetail as DocumentDetailData,
  DocumentResponse,
  MetadataEnvelope,
} from "@/api/types";
import { API_BASE } from "@/api/client";
import { useDocument } from "@/api/hooks";
import { DocumentDetail } from "@/components/document/DocumentDetail";
import { BackLink, Breadcrumb, EditorialInput, type BreadcrumbSegment } from "@/components/shared";

interface DocSearchParams {
  q?: string;
}

export const Route = createFileRoute("/document/$documentId")({
  component: DocumentPage,
  validateSearch(raw: Record<string, unknown>): DocSearchParams {
    return {
      q: typeof raw.q === "string" ? raw.q : undefined,
    };
  },
});

// The backend's authorship vocabulary is richer than the frontend's 4-class
// badge enum; map the overlap and omit anything we don't have a badge for
// (rather than render an invalid chip). `recipient`/unknown → no badge.
const AUTHORSHIP_MAP: Record<string, AuthorshipClass> = {
  authored: "authored",
  edited: "edited",
  memorialized: "memorialized",
  mentions: "mentions",
  mentioned_contemporary: "mentions",
};

/**
 * Adapt the live `DocumentResponse` (#44) to the curated `DocumentDetail`
 * shape the surface renders. Provenance is mapped faithfully from the real
 * metadata envelope — no field is invented (non-negotiable #2). Claims and
 * entities stay empty: the document endpoint surfaces `corpus_mentions`
 * (related documents), not the per-document claim/entity graph yet
 * (backend issue #30), and the component hides empty sections.
 */
function liveToDetail(r: DocumentResponse): DocumentDetailData {
  const env = r.envelope as Partial<MetadataEnvelope> | undefined;
  const dig = env?.digitization;
  const acc = env?.access;
  const ing = env?.ingest;
  const docMeta = env?.document as
    | (MetadataEnvelope["document"] & { authorship_class?: string })
    | undefined;
  const authorship = docMeta?.authorship_class
    ? AUTHORSHIP_MAP[docMeta.authorship_class]
    : undefined;
  const pageText = r.raw_text ?? (r.pages.map((p) => p.text).join("\n\n") || undefined);

  return {
    document_id: r.document_id,
    title: r.title ?? "[Untitled]",
    institution: r.institution_name,
    institution_path: r.collection_name
      ? `${r.institution_name} · ${r.collection_name}`
      : r.institution_name,
    date: r.date_display,
    document_type: r.document_type,
    authorship_class: authorship,
    // All image fetching goes through the backend proxy, which handles
    // institution-specific logic (HathiTrust, LOC, Internet Archive, etc.)
    // and returns 404 IMAGE_NOT_AVAILABLE for unsupported sources.
    source_image_url: `${API_BASE}/api/v1/document/${r.document_id}/image`,
    source_url: acc?.source_url,
    page_text: pageText,
    claims: [],
    entities: [],
    provenance: {
      institution: r.institution_name,
      collection: r.collection_name,
      document_type: r.document_type,
      date: r.date_display,
      creator: r.creator,
      digitization_method: dig?.method ?? "ocr_scan",
      digitization_date: dig?.date ?? null,
      ocr_confidence: r.ocr_confidence,
      ingest_timestamp: ing?.timestamp ?? "",
      file_hash: r.file_hash,
      access_level: acc?.level ?? "open",
      rights_statement: acc?.rights_statement ?? null,
      completeness_flags: ing?.completeness_flags ?? [],
    },
  };
}

function DocumentPage() {
  const { documentId } = Route.useParams();
  const { q } = Route.useSearch();
  const { data, isLoading, isError } = useDocument(documentId);
  const doc = data ? liveToDetail(data) : null;

  // Path: corpus › [query] › document. The query segment links back to the
  // search results when the user arrived from there (Part 11 §2).
  const segments: BreadcrumbSegment[] = [
    { label: "Corpus", to: "/", search: { mode: "graph" } },
    ...(q
      ? [{ label: `“${q}”`, to: "/search", search: { q, mode: "search" } } as BreadcrumbSegment]
      : []),
    { label: doc ? doc.title : "Document" },
  ];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-10">
      <div className="mb-6">
        <Breadcrumb segments={segments} />
      </div>
      {isLoading ? (
        <DocumentLoading />
      ) : doc ? (
        <>
          <DocumentDetail doc={doc} query={q} />
          <AskAboutDocument title={doc.title} />
        </>
      ) : (
        <div className="mx-auto max-w-xl rounded-container border border-dashed border-rule px-8 py-14 text-center">
          <p className="text-caps uppercase tracking-caps text-ink-tertiary">Document</p>
          <h1 className="mt-3 font-serif text-display leading-tight text-ink-primary">
            {isError ? "Document unavailable" : "Not found"}
          </h1>
          <p className="mx-auto mt-3 max-w-md font-serif text-lede italic leading-relaxed text-ink-secondary">
            {isError
              ? "We couldn’t load this record right now. It may not be in the corpus, or the service is briefly unavailable."
              : "This record isn’t in the corpus."}
          </p>
          <BackLink
            fallback={{ to: "/", search: { mode: "graph" } }}
            className="mt-6 inline-block text-meta text-ink-info hover:text-ink-primary"
          >
            ← Go back
          </BackLink>
        </div>
      )}
    </div>
  );
}

/** Two-pane skeleton while the document loads (aria-live for SR users). */
function DocumentLoading() {
  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]" aria-live="polite" aria-busy="true">
      <div className="h-[60vh] animate-pulse rounded-container bg-rule/30" />
      <div className="flex flex-col gap-4">
        <div className="h-7 w-3/4 animate-pulse rounded bg-rule/30" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-rule/20" />
        <div className="mt-4 h-32 animate-pulse rounded-container bg-rule/20" />
        <div className="h-32 animate-pulse rounded-container bg-rule/20" />
      </div>
      <span className="sr-only">Loading document…</span>
    </div>
  );
}

/** Bottom query input — the cross-surface affordance every non-entrance
 * surface carries (Part 12). Italic invitation, empty placeholder; submitting
 * runs a fresh search. */
function AskAboutDocument({ title }: { title: string }) {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  return (
    <div className="mx-auto mt-10 flex max-w-2xl flex-col gap-2 border-t border-rule pt-6">
      <p className="font-serif text-meta italic text-ink-tertiary">Ask about this document</p>
      <EditorialInput
        value={value}
        onChange={setValue}
        onSubmit={() => {
          const q = value.trim();
          if (!q) return;
          navigate({ to: "/search", search: { q, mode: "search" } });
        }}
        placeholder=""
        ariaLabel={`Ask about ${title}`}
        size="compact"
        submitLabel="Ask"
      />
    </div>
  );
}
